import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { CloseRounded, PreviewOutlined } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { documentCraftingFileUrls } from "../../../Api/documentCrafting";
import { DocumentCraftingLanguage } from "../../../Models/documentCrafting";
import { EmptyState } from "./DocumentCraftingUi";
import AuthImage from "./AuthImage";

export type PreviewUnit = "page" | "slide";

const ASPECT: Record<PreviewUnit, number> = {
  page: 210 / 297,
  slide: 16 / 9,
};

interface Props {
  documentId: number;
  revisionId: number;
  /** Number of page (slide) PNGs, `artefacts.pages`. */
  pageCount: number;
  /** A4 pages or presentation slides (labels and aspect ratio). */
  unit?: PreviewUnit;
  /** Shows the translation preview of the revision instead. */
  translationLanguage?: DocumentCraftingLanguage;
  /** Height of the scroll area; the pages scroll inside it. */
  maxHeight?: number | string;
}

/**
 * The rendered pages of a revision, one under the other in a scroll area.
 * Images load as they scroll into view; a click opens a page full size.
 */
const PagePreview: React.FC<Props> = ({
  documentId,
  revisionId,
  pageCount,
  unit = "page",
  translationLanguage,
  maxHeight = "75vh",
}) => {
  const { t } = useTranslation("documentCrafting");
  const [zoomed, setZoomed] = useState<number | null>(null);

  const pageUrl = (page: number) =>
    translationLanguage
      ? documentCraftingFileUrls.translationPage(
          documentId,
          revisionId,
          translationLanguage,
          page,
        )
      : documentCraftingFileUrls.revisionPage(documentId, revisionId, page);
  const pageLabel = (page: number) =>
    t(`common.preview.${unit}`, { page, total: pageCount });

  if (pageCount < 1) {
    return (
      <EmptyState
        icon={<PreviewOutlined />}
        title={t("common.preview.empty")}
      />
    );
  }

  const pages = Array.from({ length: pageCount }, (_, index) => index + 1);

  return (
    <>
      <Box
        sx={{
          maxHeight,
          overflowY: "auto",
          p: { xs: 1.5, md: 2 },
          borderRadius: "12px",
          backgroundColor: color.gray100,
          border: `1px solid ${color.gray200}`,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {pages.map((page) => (
          <Box key={page} sx={{ width: "100%", maxWidth: unit === "page" ? 720 : 960, mx: "auto" }}>
            <Typography
              sx={{ fontSize: 12, color: color.gray500, mb: 0.5, fontWeight: 600 }}
            >
              {pageLabel(page)}
            </Typography>
            <ButtonBase
              onClick={() => setZoomed(page)}
              aria-label={t("common.preview.zoom", { label: pageLabel(page) })}
              sx={{
                display: "block",
                width: "100%",
                borderRadius: "4px",
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(17, 24, 39, 0.12)",
                "&:focus-visible": { outline: `2px solid ${color.gray700}` },
              }}
            >
              <AuthImage
                url={pageUrl(page)}
                alt={pageLabel(page)}
                aspectRatio={ASPECT[unit]}
                sx={{ backgroundColor: color.white }}
              />
            </ButtonBase>
          </Box>
        ))}
      </Box>

      <Dialog
        open={zoomed !== null}
        onClose={() => setZoomed(null)}
        maxWidth="lg"
        fullWidth
      >
        {zoomed !== null && (
          <>
            <DialogTitle
              sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}
            >
              <Box component="span" sx={{ flex: 1 }}>
                {pageLabel(zoomed)}
              </Box>
              <IconButton
                onClick={() => setZoomed(null)}
                aria-label={t("common.close")}
              >
                <CloseRounded />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <AuthImage
                url={pageUrl(zoomed)}
                alt={pageLabel(zoomed)}
                lazy={false}
                aspectRatio={ASPECT[unit]}
                sx={{ backgroundColor: color.white }}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

export default PagePreview;
